import { ref } from "vue";
import { showAlert } from "@allwyn/design-system/src/modal-alert/show-alert";
import { eventBus, GlobalEvents } from "@allwyn/utils/global-events";
import { logException } from "@allwyn/utils/logger/use-app-logger";
import { NgBalance, NgFw, NgSessionLite } from "@allwyn/utils/models/ng-fw";
import { UrlUtils } from "@allwyn/utils/url";
import { useAuthentication } from "./authentication/composables/use-authentication";
import { useNg3DsRedirection } from "./ng-3ds-redirection";
import { ngHandleMmi } from "./ng-mmi";
import { useNgSession } from "./ng-session";
import { ngSessionToContact } from "./ng-session-to-contact";
import { logLogin } from "./utils/log-login";

export type DepositStatus = "succeeded" | "failed" | "aborted" | "timeout" | "pending" | "idle";

export const ngFrameworkLoadedSuccessfully = ref(false);
export const isNgUIVisible = ref(false);
export const depositStatus = ref<DepositStatus>("idle");

let hasNgFrameworkError = false;

const frameworkLoadedPromiseResolves: (() => void)[] = [];
const sessionStatusCallbacks: ((session: NgSessionLite) => void)[] = [];
let openPageCallback: ((key: string, customInfo: any) => void) | null = null;

export async function useNg(openPage: typeof openPageCallback) {
    openPageCallback = openPage;
    useNgSession().init();
    useNgLoader();
    // await ngFrameworkLoaded();
}

/**
 * ng_hs is ng framework's way to notify web page when user state changes
 * it is using callbacks
 */
async function useNgHs() {
    try {
        ng_hs.onBalanceUpdated((balance: NgBalance) => {
            useNgSession().balance.value = balance;
        });

        ng_hs.onSessionStatusUpdate((session: NgSessionLite) => {
            if (session.Status == "Login") {
                // we need to do that to load more information from NG framework
                useNgSession().refreshSession();
                const { session } = useNgSession();

                if (!session.value) return;

                logLogin(ngSessionToContact(session.value), session.value.PlayerID);
            } else {
                useNgSession().clearSession();
            }
            sessionStatusCallbacks.forEach((f) => f(session));
        });

        ng_hs.registrationAborted(() => eventBus.emit(GlobalEvents.WebLoginAborted));

        ng_hs.openPage(openPageCallback!);

        ng_hs.clearURLParams((urlParams: string[]) => {
            UrlUtils.clearUrlParams(urlParams);
        });

        ng_hs.onStateChange((state) => {
            isNgUIVisible.value = state === "active";
        });

        ng_hs.depositPending(() => {
            depositStatus.value = "pending";
        });

        ng_hs.depositSucceeded(() => {
            depositStatus.value = "succeeded";
        });

        ng_hs.depositFailure(() => {
            depositStatus.value = "failed";
        });

        ng_hs.depositAborted(() => {
            depositStatus.value = "aborted";
        });

        ng_hs.depositTimeout(() => {
            depositStatus.value = "timeout";
        });

        useNg3DsRedirection().registerNgHsCallback();
    } catch (e: any) {
        logException("neogames", e, { message: "An error has occurred while adding ng_hs callbacks" });
    }
}

export function getNgTimeDuration(ngActionTimestamp: string, ngServerTime: string): number {
    const start = Date.parse(ngActionTimestamp);
    const server = Date.parse(ngServerTime);
    const now = Date.now();
    return start - server + now;
}

function useNgIdentityVerification() {
    const parameter = UrlUtils.getQueryString("identityverification", null, false);
    if (parameter) {
        ng_fw.showPersonalInfoVerification();
    }
}

function useNgLoader() {
    if (ngLoader.ngIsReady()) {
        frameworkLoaded();
    } else if (ngLoader.ngError) {
        frameworkLoadError(ngLoader.ngErrorMessage);
    } else {
        document.addEventListener("fwInitError", (reason) => frameworkLoadError(reason as any));
        document.addEventListener("fwReady", frameworkLoaded);
    }
}

export function ngFrameworkLoaded(): Promise<void> {
    return new Promise((res, rej) => {
        if (ngFrameworkLoadedSuccessfully.value) {
            res();
        } else if (hasNgFrameworkError) {
            rej();
        } else {
            frameworkLoadedPromiseResolves.push(res);
        }
    });
}

/**
 * @deprecated Use watch on useNgSession().isLoggedIn instead
 * @param f callback
 */
export function addSessionStatusUpdateCallback(f: (status: NgSessionLite) => void): void {
    sessionStatusCallbacks.push(f);
}

async function frameworkLoaded() {
    ngFrameworkLoadedSuccessfully.value = true;
    useNgHs();
    ngHandleMmi();
    useNgIdentityVerification();

    const { refreshSession, refreshBalance, restoreFromWebView } = useNgSession();
    refreshSession();
    await useAuthentication().keepSessionAlive();
    await refreshBalance();
    await restoreFromWebView();
    for (const resolve of frameworkLoadedPromiseResolves) {
        resolve();
    }
}

function frameworkLoadError(errorMessage: string) {
    ngFrameworkLoadedSuccessfully.value = false;
    hasNgFrameworkError = true;
    logException("NG framework", errorMessage);
    showAlert({
        title: "Tady něco nehraje…",
        subText:
            "Je nám líto, ale nepodařilo se správně načíst stránku. Pokud i poté potíže přetrvávají, jedná se o chybu na naší straně, na jejímž odstranění pracujeme.",
    });
}

export function ngAction(actionName: keyof NgFw) {
    if (ngFrameworkLoadedSuccessfully.value) {
        (ng_fw[actionName] as any)();
    }
}
